import {
  BLANK_ENVIRONMENTAL_FACTORS,
  EMPTY_APP_ACTION,
  SET_ENVIRONMENTAL_FACTORS,
  SET_MODEL,
} from 'actions';
import { hitApi, ObservableAjax } from '__common/utils/api';
import { ProjectConfigurationActionTypes } from 'actionsConstants';
import { ENV_LOADING_FINISHED, SET_ENVIRONMENTAL_FACTORS_ACTION, SET_PROJECT_OPTION, SET_RAIL_ARRANGEMENT_VALID_LIST, FETCH_VALID_RAIL_ARRANGEMENT_LIST, SET_SEISMIC_SETBACK_DISTANCE } from './projectConfigurationActions';
import { Observable, EMPTY, from, of } from 'rxjs';
import nprogress from 'nprogress';
import { AnyAction } from 'redux';
import { StateObservable } from 'redux-observable';
import { safeOfType as ofType } from '__common/utils/safeOfType';
import { catchError, distinctUntilChanged, filter, ignoreElements, map, pairwise, startWith, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { Action } from '__common/store/action';
import { updateProjectOption, validateSetback, setProjectOptionOnInputUnitChange } from './utils/updateProjectOption';
import { apiField as snowLoadField } from 'projectDesign/components/projectConfiguration/fields/types/snowLoad';
import { apiField as windSpeedField } from 'projectDesign/components/projectConfiguration/fields/windSpeed';
import { apiField as seismicSsApiField } from 'projectDesign/components/projectConfiguration/fields/types/seismicSs';
import { apiField as seismicS1ApiField } from 'projectDesign/components/projectConfiguration/fields/seismicS1';
import { apiField as seismicSdsApiField } from 'projectDesign/components/projectConfiguration/fields/seismicSds';
import { apiField as seismicSd1ApiField } from 'projectDesign/components/projectConfiguration/fields/seismicSd1';
import { apiField as numericCoefficientField } from "projectDesign/components/projectConfiguration/fields/numericalCoefficient"
import { changeRailArrangementTypeWarning, isCanadianZipcode, changeSetbackDistanceWarning } from './utils/validation';
import { isAscender, isGroundProduct, isSolarMount, isRM10, isCanadianChangesProduct, isEcoFoot2Plus, isRM5, isSMTiltPR } from '__common/constants/products';
import store, { dispatch } from '__common/store';
import { InputUnit, isMetricUnit } from 'engineering/components/engineeringProjectDocuments/utils/unitTypes';
import { cmsToFeets, psfToKpa } from '__common/calculations/unitConversions';
import { round } from 'lodash';
import { metersToFeets } from '__common/calculations/metersToFeets';
import { get_numerical_coefficient } from './utils/coefficients';
import { isASCE722 } from '__common/constants/buildingCodes';

let notFoundsZipCodes = [];
const ParapetHeightGreaterThan12Values = [3];

// Use the same full-width navigation progress bar as other application requests.
export function environmentalFactorsProgress(action$: Observable<AnyAction>, state$: StateObservable<appState>): Observable<never> {
  return state$.pipe(
    map(state => !!state.projectConfiguration.envFactorsLoading),
    startWith(false),
    distinctUntilChanged(),
    pairwise(),
    tap(([, loading]) => {
      if (typeof document !== 'undefined') {
        document.body.classList.toggle('environmental-factors-loading', loading);
      }
      if (loading) {
        nprogress.start();
      } else {
        nprogress.done();
      }
    }),
    ignoreElements(),
  );
}

export function getEnvironmentalFactors(action$: Observable<AnyAction>, state$: StateObservable<appState>): Observable<AnyAction> {
  return action$.pipe(
    ofType(ProjectConfigurationActionTypes.GET_ENVIRONMENTAL_FACTORS),
    switchMap((action) => {
      const { lat, lng, zipcode, buildingCode, productId, risk_category } = action.payload;
      if (isCanadianZipcode(zipcode)) {
        dispatch(SET_ENVIRONMENTAL_FACTORS(zipcode, productId, {
          wind_speed: 185,
          snow_load: round(psfToKpa(50), 2),
          seismic_ss: 1,
          seismic_s1: 0.39,
          elevation: 0,
          building_code: buildingCode || 2,
          wind_special: false,
        }));
        return EMPTY;
      } else {
        const { inputUnit } = state$.value.projectConfiguration;
        return ObservableAjax({
          until$: action$.pipe(ofType(ProjectConfigurationActionTypes.SET_ENVIRONMENTAL_FACTORS)),
          onSuccess: (factors: { wind_speed: number, wind_special: boolean, snow_load: number, elevation: number, seismic_s1: number, seismic_ss: number, seismic_sds?: number, seismic_sd1?: number, tornado?: any, tornado_speed?: number }) => {
            const { projectConfiguration: { projectEnvConfig } } = state$.value;
            const { imported_project, wind_speed, snow_load, elevation, seismic_ss, building_code, shortest_building_length = 0, building_length = 0, env_factors_for } = projectEnvConfig as any;
            const path = (typeof window !== 'undefined' && window.location && window.location.pathname) || '';
            const isLite = !!(state$.value.app && state$.value.app.isUbuilderLite) || path.includes('/lite/');
            const apiSeismicSs = factors.seismic_ss;
            if (imported_project || (isLite && env_factors_for === zipcode)) {
              // For Lite, a same-zip refetch is only triggered by a risk-category
              // or building-code change, which must refresh wind_speed / snow_load
              // from the hazard API (matching legacy). Preserving them here would
              // feed stale values to updateOnEnvironmentalFactorsUpdate (which
              // writes them via updateProjectOption), so skip the swap for Lite.
              if (wind_speed && !isLite) {
                factors['wind_speed'] = wind_speed;
              }
              if (snow_load && !isLite) {
                factors['snow_load'] = snow_load;
              }
              if (elevation) {
                factors['elevation'] = elevation;
              }
              if (seismic_ss >= 0) {
                factors['seismic_ss'] = seismic_ss;
              }
            }
            if (building_code==4 && (risk_category==3 || risk_category==4) && factors['tornado'] && 'tornado_val1' in factors['tornado']){
              let area=shortest_building_length*building_length;
              factors['tornado_speed']=factors['tornado']['tornado_val1'];
              if ( area <= 2000 ){
                factors['tornado_speed'] = factors['tornado']['tornado_val2'];
              }
              if ( area <= 10000 ){
                factors['tornado_speed'] = factors['tornado']['tornado_val3'];
              }
              if ( area <= 40000 ){
                factors['tornado_speed'] = factors['tornado']['tornado_val4'];
              }
              if ( area <= 100000 ){
                factors['tornado_speed'] = factors['tornado']['tornado_val5'];
              }
              if ( area <= 250000 ){
                factors['tornado_speed'] = factors['tornado']['tornado_val6'];
              }
              if ( area <= 1000000 ){
                factors['tornado_speed'] = factors['tornado']['tornado_val7'];
              }
              if ( area <= 4000000 ){
                factors['tornado_speed'] = factors['tornado']['tornado_val8'];
              }
              // tornado_speed is persisted as an integer (Project.tornado_speed
              // is a Django IntegerField). The hazard API returns fractional
              // values (e.g. 97.832), which the save serializer rejects with
              // "A valid integer is required". Round here so both legacy and
              // Lite store an integer.
              if (factors['tornado_speed'] !== undefined && factors['tornado_speed'] !== null) {
                factors['tornado_speed'] = Math.round(factors['tornado_speed']);
              }
            }

            dispatch(SET_ENVIRONMENTAL_FACTORS(zipcode, productId, factors));
            // UBuilder Lite — re-apply seismic_s1 after the reducer ran.
            // The shared SET_ENVIRONMENTAL_FACTORS reducer deletes
            // seismic_s1 for products outside its productsWithSeismic_S1
            // allow-list (which excludes NXT and SM), so for Lite the value
            // would land empty even though the env-factors API returned it.
            // Lite surfaces S1 in its Location section regardless of
            // product, so we redispatch via SET_PROJECT_OPTION so the
            // legacy reducer path stays untouched.
            //
            // The store flag `app.isUbuilderLite` is only set by
            // UBuilderLiteContainer.componentDidMount (workspace mount), so
            // it's *false* during the dashboard's NewProjectModal create
            // flow. Fall back to the URL path so dashboard fetches also
            // capture s1 (otherwise new Lite projects land with s1=null
            // and never recover until a workspace-side refetch).
            if (isLite && factors && factors.seismic_s1 !== undefined && factors.seismic_s1 !== null) {
              store.dispatch(SET_PROJECT_OPTION('seismic_s1', factors.seismic_s1));
            }
            // UBuilder Lite — re-apply the remaining seismic values with the
            // fresh hazard-API data. On a building-code change the zipcode is
            // unchanged, so env_factors_for === zipcode and the shared
            // SET_ENVIRONMENTAL_FACTORS reducer early-returns without writing
            // anything (and the preserve block above swaps in the stale
            // stored seismic_ss). Lite's Location section surfaces Ss/S1/Sds
            // regardless of product, so redispatch via SET_PROJECT_OPTION —
            // matching the seismic_s1 handling above — so they reflect the
            // latest hazard data instead of the stale values. Use the
            // captured API seismic_ss since factors.seismic_ss may have been
            // overwritten by the preserve block.
            if (isLite && apiSeismicSs !== undefined && apiSeismicSs !== null) {
              store.dispatch(SET_PROJECT_OPTION('seismic_ss', apiSeismicSs));
            }
            if (isLite && factors && factors.seismic_sds !== undefined && factors.seismic_sds !== null) {
              store.dispatch(SET_PROJECT_OPTION('seismic_sds', factors.seismic_sds));
            }
            if (isLite && factors && factors.seismic_sd1 !== undefined && factors.seismic_sd1 !== null) {
              store.dispatch(SET_PROJECT_OPTION('seismic_sd1', factors.seismic_sd1));
            }
            // UBuilder Lite — re-apply tornado_speed with the fresh hazard-API
            // data. On a risk-category or building-code change the zipcode is
            // unchanged, so env_factors_for === zipcode and the shared
            // SET_ENVIRONMENTAL_FACTORS reducer early-returns without writing
            // anything. wind_speed / snow_load don't need a re-apply here — the
            // preserve block above skips them for Lite so they flow through
            // updateOnEnvironmentalFactorsUpdate → updateProjectOption with the
            // fresh values (matching legacy). tornado_speed has no such writer,
            // so redispatch it via SET_PROJECT_OPTION.
            if (isLite && factors && factors['tornado_speed'] !== undefined && factors['tornado_speed'] !== null) {
              store.dispatch(SET_PROJECT_OPTION('tornado_speed', factors['tornado_speed']));
            }
          },
          onErrorAction: () => {
            if (isASCE722(buildingCode)) {
              dispatch(BLANK_ENVIRONMENTAL_FACTORS(zipcode, productId));
              if (!notFoundsZipCodes.includes(zipcode)) {
                notFoundsZipCodes.push(zipcode);
              }
            } else if (!notFoundsZipCodes.includes(zipcode)) {
              dispatch(BLANK_ENVIRONMENTAL_FACTORS(zipcode, productId));
              notFoundsZipCodes.push(zipcode);
            } else {
              dispatch(ENV_LOADING_FINISHED());
            }
          },
          link: hitApi('get', getLinkToEnvFactors(lat, lng, zipcode, buildingCode, risk_category, productId, inputUnit ?? (isAscender(productId) ? InputUnit.METRIC : InputUnit.CUSTOMARY))),
        });
      }
    })
  );
}

export function updateOnEnvironmentalFactorsUpdate(action$: Observable<Action<SET_ENVIRONMENTAL_FACTORS_ACTION>>, state$: StateObservable<appState>): Observable<AnyAction> {
  return action$.pipe(
    ofType(ProjectConfigurationActionTypes.SET_ENVIRONMENTAL_FACTORS),
    switchMap(({ payload: { factors, productId, zipcode } }) => {
      const { projectConfiguration: { projectEnvConfig, projectVersion, inputUnit } } = state$.value;
      // Keep loading active until the asynchronous field updates have populated
      // all values, including Sds and Sd1 when switching to ASCE 7-22.
      const updates = [
        updateProjectOption(snowLoadField, factors.snow_load, zipcode, productId, projectEnvConfig),
        updateProjectOption(windSpeedField, factors.wind_speed, zipcode, productId, projectEnvConfig),
        updateProjectOption(seismicSsApiField, factors.seismic_ss, zipcode, productId, projectEnvConfig),
        updateProjectOption(seismicS1ApiField, factors.seismic_s1, zipcode, productId, projectEnvConfig),
      ];
      if (isASCE722(projectEnvConfig.building_code)) {
        updates.push(
          updateProjectOption(seismicSdsApiField, factors.seismic_sds, zipcode, productId, projectEnvConfig),
          updateProjectOption(seismicSd1ApiField, factors.seismic_sd1, zipcode, productId, projectEnvConfig),
        );
      }
      const numericalCoefficient = get_numerical_coefficient(projectEnvConfig.building_code, factors.elevation, inputUnit, zipcode, projectVersion);
      updates.push(updateProjectOption(numericCoefficientField, numericalCoefficient, zipcode, productId, projectEnvConfig));
      return from(Promise.all(updates)).pipe(
        map(() => {
          const { projectEnvConfig: updatedConfig } = state$.value.projectConfiguration;
          validateSetback(productId, updatedConfig, projectVersion, inputUnit);
          return ENV_LOADING_FINISHED();
        }),
        catchError(error => {
          console.error('Could not populate environmental parameters', error);
          return of(ENV_LOADING_FINISHED());
        }),
        // An earlier response must not stop the loader for a newer lookup.
        takeUntil(action$.pipe(ofType(ProjectConfigurationActionTypes.GET_ENVIRONMENTAL_FACTORS))),
      );
    })
  );
}

export function saveProjectTitle(action$: Observable<AnyAction>) {
  return action$.pipe(
    ofType(ProjectConfigurationActionTypes.SAVE_PROJECT_TITLE),
    switchMap((action) => {
      const { projectId, customTitle } = action.payload;
      return ObservableAjax({
        until$: action$.pipe(ofType(ProjectConfigurationActionTypes.SAVE_PROJECT_TITLE)),
        onSuccess: EMPTY_APP_ACTION,
        onErrorMessage: 'Cannot save project name',
        link: hitApi('put', `api/v1/project_title/${projectId}/`, { custom_title: customTitle }),
      });
    })
  );
}

export function updateFlyoutOptionsOnInputUnitChange(action$: Observable<AnyAction>, state$: StateObservable<appState>) : Observable<AnyAction>{
  return action$.pipe(
    ofType(ProjectConfigurationActionTypes.TOGGLE_INPUT_UNIT),
    filter(() => isCanadianChangesProduct(state$.value.projectConfiguration.productId) || isSolarMount(store.getState().projectConfiguration.productId) || isSMTiltPR(state$.value.projectConfiguration.productId)),
    switchMap((action) => {
      const { projectConfiguration: { productId, inputUnit }, moduleSelector: { selectedModelId, modelData } } = state$.value;
      setProjectOptionOnInputUnitChange();
      const customModel = selectedModelId === 1000001 ? modelData : undefined;
      dispatch(SET_MODEL(selectedModelId, customModel, productId, inputUnit, true));
      return EMPTY;
    })
  );
}

interface RailArrangementApiPayload {
  railArrangementTypeValidList: number[];
}

export function fetchRailArrangementListWhenClampCheckFailed(action$: Observable<AnyAction>, state$: Observable<appState>): Observable<any> {
  return action$.pipe(
    ofType(ProjectConfigurationActionTypes.SET_CLAMP_CHECK_FAILED),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      const {
        projectConfiguration: { projectId },
      } = state;
      return ObservableAjax({
        onSuccess: [FETCH_VALID_RAIL_ARRANGEMENT_LIST.bind(null, projectId), ({ railArrangementTypeValidList }: RailArrangementApiPayload) => {
          changeRailArrangementTypeWarning();
          return SET_RAIL_ARRANGEMENT_VALID_LIST(railArrangementTypeValidList);
        }],
        onErrorMessage: 'Could not fetch valid rail arrangement list',
        link: hitApi('get', `api/v1/valid_rail_arrangement_list/${projectId}`),
      });
    })
  );
}

interface SetbackDistanceApiResponse {
  seismic_setback_ft: number;
  seismic_setback_satisfied: boolean;
}

export function validateSetbackDistance(action$: Observable<AnyAction>, state$: Observable<appState>): Observable<any> {
  return action$.pipe(
    ofType(ProjectConfigurationActionTypes.FETCH_MINIMUM_VALID_SETBACK_DISTANCE),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      const {
        projectConfiguration: { productId, inputUnit, projectEnvConfig: { seismic_ss, seismic_s1, risk_category, building_code, soil_class, setback_distance, parapet_height, parapet_height_input } },
      } = state;
      const isParapetHeightGreaterThan12 = isRM10(productId) || isEcoFoot2Plus(productId) ? ParapetHeightGreaterThan12Values.includes(parapet_height) : parapet_height_input > 12;
      const setback = isMetricUnit(inputUnit) ? round(cmsToFeets(setback_distance), 1) : setback_distance;
      return ObservableAjax({
        onSuccess: ({ seismic_setback_ft, seismic_setback_satisfied }: SetbackDistanceApiResponse) => {
          const seismic_setback = isMetricUnit(inputUnit) ? round(metersToFeets(seismic_setback_ft), 1) : seismic_setback_ft;
          changeSetbackDistanceWarning(seismic_setback, seismic_setback_satisfied, productId);
          return SET_SEISMIC_SETBACK_DISTANCE(seismic_setback);
        },
        onErrorMessage: 'Could not validate setback distance',
        link: hitApi('get', `api/v1/valid_setback_distance/?seismic_ss=${seismic_ss}&seismic_s1=${seismic_s1}&risk_category=${risk_category}&building_code=${building_code}&soil_class=${soil_class}&setback_distance=${setback}&isParapetHeightGreaterThan12=${isParapetHeightGreaterThan12}`),
      });
    })
  );
}

export function clearFoundZipCodeForEnvFactors() {
  notFoundsZipCodes = [];
}

const getLinkToEnvFactors = (lat: number, lng: number, zipcode: string, buildingCode: number, risk_category: number, productId: number, inputUnit?: number) => {
  if (risk_category > 0) {
    return `api/v1/get_environmental_factors/?lat=${lat}&lon=${lng}&zipcode=${zipcode}&building_code=${buildingCode}&risk_category=${risk_category}&product_id=${productId}&input_unit=${inputUnit}`;
  }

  if (isGroundProduct(productId)) {
    const riskCategory = 1;
    return `api/v1/get_environmental_factors/?lat=${lat}&lon=${lng}&zipcode=${zipcode}&building_code=${buildingCode}&risk_category=${riskCategory}&product_id=${productId}`;
  }

  return `api/v1/get_environmental_factors/?lat=${lat}&lon=${lng}&zipcode=${zipcode}&building_code=${buildingCode}&input_unit=${inputUnit}`;
};
